/**
 * GoogleSetup.tsx
 * Shown to first-time Google OAuth users who don't have a vendor row yet.
 * Collects business name + WhatsApp number then creates the vendor.
 */

import React, { useState } from 'react';
import { User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Vendor } from '../types';

interface GoogleSetupProps {
  email: string;
  authUserId: string;
  onSuccess: (vendor: Vendor) => void;
}

export default function GoogleSetup({ email, authUserId, onSuccess }: GoogleSetupProps) {
  const [businessName, setBusinessName] = useState('');
  const [whatsapp, setWhatsapp]         = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const slug = businessName.toLowerCase().replace(/[^a-z0-9]/g, '').trim();
    if (!slug) { setError('Business name must contain letters or numbers.'); return; }

    let cleanPhone = whatsapp.replace(/[^0-9]/g, '');
    if (cleanPhone.startsWith('0')) cleanPhone = '233' + cleanPhone.substring(1);
    else if (!cleanPhone.startsWith('233') && cleanPhone.length === 9) cleanPhone = '233' + cleanPhone;
    if (cleanPhone.length < 9 || cleanPhone.length > 15) {
      setError('Please enter a valid WhatsApp number (e.g. 0244123456).');
      return;
    }

    setLoading(true);
    try {
      const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vendors`;
      const res = await fetch(fnUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          business_name: slug,
          email,
          whatsapp_number: cleanPhone,
          auth_user_id: authUserId,
        }),
      });

      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Could not create vendor profile.');
      onSuccess(body);
    } catch (err: any) {
      setError(err.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl shadow-xl border border-slate-100 w-full max-w-md p-8">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="p-2 bg-orange-500 rounded-xl text-white">
            <User className="w-4 h-4" />
          </div>
          <div>
            <h2 className="font-display font-bold text-lg text-slate-900">Almost there!</h2>
            <p className="text-[11px] text-slate-400">Just set up your catalog details</p>
          </div>
        </div>

        <p className="text-xs text-slate-500 mb-5">
          Signed in as <span className="font-semibold text-slate-700">{email}</span>. Now pick your catalog URL and WhatsApp number.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">
              Business Handle
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-xs text-slate-400 font-mono">vendorpulse/</span>
              <input
                type="text"
                placeholder="e.g. aduaboutique"
                value={businessName}
                onChange={e => setBusinessName(e.target.value)}
                className="w-full bg-white/60 border border-slate-200 rounded-xl pl-28 pr-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all font-mono"
                required
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">
              WhatsApp Number
            </label>
            <input
              type="tel"
              placeholder="e.g. 0244123456"
              value={whatsapp}
              onChange={e => setWhatsapp(e.target.value)}
              className="w-full bg-white/60 border border-slate-200 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all font-mono"
              required
            />
            <span className="text-[9px] text-slate-400 mt-1 block">
              💡 0xx numbers auto-converted to 233xx format.
            </span>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-red-700 text-xs">
              ⚠️ {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 active:scale-95 text-white font-semibold text-xs rounded-xl uppercase tracking-wider transition-all shadow-lg shadow-orange-500/20"
          >
            {loading ? 'Creating catalog...' : 'Launch My Catalog ₵'}
          </button>
        </form>
      </div>
    </div>
  );
}
